import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Notification, NotificationCategory } from '@proref/types';
import { useMarkNotificationsRead, useNotificationBellQuery } from '../../hooks/use-notifications';
import { timeAgo } from '../../lib/format';
import { Button } from '../shared/Button';
import { Icon } from '../shared/Icons';

// Category → the icon that fronts a row. Coarse on purpose: the title carries the specifics, and a
// distinct glyph per notification TYPE (there are two dozen) would be visual noise rather than a
// signal. Kept here rather than in Icons so adding a category is one edit in one file.
const CATEGORY_ICON: Record<NotificationCategory, keyof typeof Icon> = {
  daily: 'spark',
  exams: 'flask',
  webinars: 'calendar',
  discussion: 'send',
  admin: 'building',
  platform: 'chart',
  account: 'lock',
};

// A badge past this reads as a count nobody acts on — at that point the number stops being
// information and the user needs the full page, not a bigger pill.
const BADGE_CAP = 99;

interface NotificationRowProps {
  notification: Notification;
  // Whether to render the unread marker. NOT `read_at == null`: opening the dropdown marks these
  // read immediately, so binding the dot to live state would blank every marker the instant the
  // panel appeared — the user would never see what was new. See `unreadAtOpen` below.
  showUnread: boolean;
  onNavigate: () => void;
}

function NotificationRow({ notification, showUnread, onNavigate }: NotificationRowProps) {
  const Glyph = Icon[CATEGORY_ICON[notification.category] ?? 'bell'];

  const body = (
    <>
      <span
        className={
          'mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full ' +
          (showUnread ? 'bg-accent-tint text-accent' : 'bg-surface-2 text-muted')
        }
      >
        <Glyph className="h-3.5 w-3.5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-start gap-2">
          <span className="min-w-0 flex-1 text-[13px] font-medium leading-snug text-ink">
            {notification.title}
          </span>
          {showUnread && (
            <span
              className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-accent"
              aria-label="Unread"
            />
          )}
        </span>
        {notification.body && (
          <span className="mt-0.5 block line-clamp-2 text-[12px] leading-snug text-muted">
            {notification.body}
          </span>
        )}
        <span className="mt-1 block text-[11px] text-muted-2">{timeAgo(notification.created_at)}</span>
      </span>
    </>
  );

  const className =
    'flex w-full gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-surface-2 ' +
    (showUnread ? 'bg-accent-tint/25' : '');

  // A notification without a link has nowhere to go ("your streak broke") — render it as a plain
  // row rather than a dead link, so it neither looks clickable nor traps a keyboard user on a
  // control that does nothing.
  if (!notification.link) {
    return <div className={className}>{body}</div>;
  }

  return (
    <Link to={notification.link} className={className} onClick={onNavigate}>
      {body}
    </Link>
  );
}

// The topbar bell: unread badge + a dropdown of the newest entries.
//
// Dropdown rather than a route: checking notifications is a glance that should not cost the page
// you are on. The full list at /notifications is one click away for when it is more than a glance.
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useNotificationBellQuery();
  const markRead = useMarkNotificationsRead();
  const wrapRef = useRef<HTMLDivElement>(null);

  const items = useMemo(() => data?.items ?? [], [data]);
  const unreadCount = data?.meta.unread_count ?? 0;

  // The ids that were unread at the moment the panel opened, frozen for as long as it stays open.
  // Opening marks them read server-side straight away, so without this snapshot the refetch that
  // follows would strip every unread marker while the user is still looking at the list.
  const [unreadAtOpen, setUnreadAtOpen] = useState<Set<string>>(new Set());

  // Mark what the dropdown is showing as read, once per open.
  //
  // Scoped to the visible ids rather than `all`: the badge counts the whole inbox, and clearing
  // entries the user never saw — because they were on page two — would lose them silently.
  useEffect(() => {
    if (!open) return;
    const unread = items.filter((n) => n.read_at === null).map((n) => n.id);
    setUnreadAtOpen(new Set(unread));
    if (unread.length > 0) markRead.mutate({ ids: unread });
    // Deliberately keyed on `open` alone. Including `items` would re-fire on the invalidation this
    // very mutation triggers, and including `markRead` would re-fire on every render of it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Close on Escape and on a click outside. Both are what a dropdown is expected to do, and
  // without the outside-click the panel survives navigating away via a link in the topbar.
  useEffect(() => {
    if (!open) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    function onPointerDown(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  return (
    <div className="relative flex-none" ref={wrapRef}>
      <Button
        variant="ghost"
        icon
        className="h-9 w-9 flex-none"
        onClick={() => setOpen((v) => !v)}
        title="Notifications"
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Icon.bell className="w-[15px] h-[15px]" />
        {unreadCount > 0 && (
          // aria-hidden: the count is already in the button's accessible name above, and a badge
          // read out separately would announce the number twice.
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-full border border-bg bg-accent px-1 text-[9px] font-semibold leading-none text-accent-ink"
          >
            {unreadCount > BADGE_CAP ? `${BADGE_CAP}+` : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div
          role="menu"
          aria-label="Notifications"
          // Right-anchored and width-capped to the viewport: on a phone the topbar bell sits a few
          // pixels from the right edge, and a fixed-width panel would hang off-screen.
          className="absolute right-0 top-11 z-40 w-[min(360px,calc(100vw-2rem))] overflow-hidden rounded-md border border-line-2 bg-surface shadow-2"
        >
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <span className="text-[13px] font-semibold text-ink">Notifications</span>
            {unreadCount > 0 && (
              <button
                type="button"
                className="text-[12px] text-muted transition-colors hover:text-ink"
                onClick={() => markRead.mutate({ all: true })}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[min(60vh,420px)] overflow-y-auto">
            {isLoading ? (
              <p className="px-3 py-6 text-center text-[12px] text-muted">Loading…</p>
            ) : items.length === 0 ? (
              <p className="px-3 py-8 text-center text-[12px] text-muted">
                Nothing yet. Streaks, exam results and webinar updates will show up here.
              </p>
            ) : (
              items.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  showUnread={unreadAtOpen.has(n.id)}
                  onNavigate={() => setOpen(false)}
                />
              ))
            )}
          </div>

          <div className="border-t border-line px-3 py-2 text-center">
            <Link
              to="/notifications"
              className="text-[12px] font-medium text-accent hover:underline"
              onClick={() => setOpen(false)}
            >
              See all
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
